/**
 * FRONSYSTEM / волна 4.1: единый слой для текста ошибок API в UI (STYLESYSTEM, zero-trust).
 * — Если от бэкенда уже пришла короткая строка с кириллицей — показываем её.
 * — Иначе пробуем сопоставить известные англ. фразы (legacy/интеграции).
 * — Иначе — только fallback (без сырого англ. текста в toast).
 */

const MAX_USER_ERROR_LEN = 400;

/** Ключ — trim + lowerCase исходной строки (латиница и типовые ответы бэкенда). */
const KNOWN_EN_ERRORS_LC = {
  unauthorized: 'Требуется авторизация',
  forbidden: 'Доступ запрещён',
  'not found': 'Данные не найдены',
  'internal server error': 'Внутренняя ошибка сервера',
  'bad request': 'Некорректный запрос',
  'query is required': 'Укажите поисковый запрос',
  'file is required': 'Выберите файл',
  'audio file is required': 'Выберите аудиофайл',
  'token is required': 'Требуется токен',
  'bearer token required': 'Требуется токен авторизации',
  'widget token required': 'Требуется токен виджета',
  'no token in waygpt response': 'Ответ консультанта неполный. Обновите страницу и повторите.',
  'admin access required': 'Нужны права администратора',
  'super admin access required': 'Нужны права суперадминистратора',
  'unsupported maintenance job': 'Неподдерживаемая служебная операция',
  'waygpt platform settings are not configured': 'Консультант ещё не подключён.',
  'ai waygpt not configured': 'Консультант ещё не подключён.',
  'no settings to update': 'Нет настроек для сохранения',
  'product search in chat is disabled': 'Поиск товаров в чате отключён',
  'provide query or queries': 'Укажите запрос или список запросов',
  'supplier not found': 'Поставщик не найден',
  'invalid or missing supplier_type': 'Некорректный или не указан тип поставщика',
  'name is required': 'Укажите название',
  'no fields to update': 'Нет полей для обновления',
  'adapter not found or supplier not configured': 'Адаптер не найден или поставщик не настроен',
  'core_scheduler_trigger_token is not configured': 'Не настроен токен планировщика (CORE)',
  'invalid credentials': 'Неверный логин или пароль',
  'access denied': 'Доступ запрещён',
  'session expired': 'Сессия истекла',
  'validation failed': 'Проверка данных не пройдена',
  'too many requests': 'Слишком много запросов',
  'service unavailable': 'Сервис временно недоступен',
  'network error': 'Ошибка сети',
  archive_quota_exceeded: 'Лимит архива товаров исчерпан. Удалите старые архивные товары (окончательное удаление), чтобы освободить место.',
  product_quota_exceeded: 'Нельзя восстановить товар: лимит основных товаров исчерпан. Освободите слот (архив или удаление) или увеличьте тариф.',
  price_tampered: 'Цена на экране не совпала с расчётом сервера. Корзина сохранена — обновите цены и повторите оплату.',
  offline_price_changed: 'Пока касса была без связи, цена товара изменилась. Продажа не проведена — проверьте сумму и повторите.',
  pricing_unavailable: 'Сервис цен временно недоступен. Повторите, когда связь восстановится.',
  price_unavailable: 'Для этой позиции нет цены. Продажа недоступна, пока цена не задана.',
  offline_snapshot_required: 'Офлайн-продажа без подтверждённой сервером цены недоступна. Восстановите связь и повторите расчёт.',
  offline_snapshot_expired: 'Подтверждение цены истекло. Восстановите связь, пересчитайте чек и повторите оплату.',
  offline_snapshot_tampered: 'Снимок цены повреждён. Восстановите связь и повторите расчёт.',
};

function tryMapEnglishPhrase(s) {
  const t = String(s).trim();
  if (!t || t.length >= MAX_USER_ERROR_LEN) return null;
  const lc = t.toLowerCase();
  if (KNOWN_EN_ERRORS_LC[lc]) return KNOWN_EN_ERRORS_LC[lc];
  return null;
}

/**
 * Текст ошибки из ответа API для показа пользователю.
 * @param {unknown} raw — строка из error/message или пусто
 * @param {string} fallback — русский текст по умолчанию
 */
export function pickSafeRussianBackendMessage(raw, fallback) {
  const fb = typeof fallback === 'string' && fallback.length > 0 ? fallback : 'Произошла ошибка. Попробуйте ещё раз.';
  if (raw == null) return fb;
  const s = typeof raw === 'string' ? raw : String(raw);
  if (!s || s.length >= MAX_USER_ERROR_LEN) return fb;
  if (/[а-яА-ЯёЁ]/.test(s)) {
    const cleaned = s
      .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, '')
      .replace(/ВЭЙГПТ|WayGPT|DaData|Fernet|Control Center/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
    return cleaned || fb;
  }
  const mapped = tryMapEnglishPhrase(s);
  if (mapped) return mapped;
  return fb;
}

/**
 * Fallback по HTTP-статусу, если тело ответа пустое или на англ. без словаря.
 * @param {number} [status]
 */
export function pickHttpStatusRussianFallback(status) {
  if (status === 401) return 'Требуется авторизация';
  if (status === 402) return 'Требуется пополнение баланса или оплата';
  if (status === 403) return 'У вас нет прав для этого действия';
  if (status === 404) return 'Запрашиваемые данные не найдены';
  if (status === 409) return 'Конфликт данных. Обновите страницу и повторите действие';
  if (status === 422) return 'Проверьте введённые данные';
  if (status === 429) return 'Слишком много запросов. Подождите немного';
  if (status >= 500) return 'Сервер временно недоступен. Повторите попытку позже';
  return 'Произошла ошибка. Попробуйте ещё раз';
}
